import { useCallback, useEffect, useRef, useState } from 'react';
import { getSalesforceSellerAccountListUrl } from '../constants';

export type SellerAccountRecord = {
  Account_ID__c: string;
  Customer_Type__c?: string;
  Id: string;
  isFinanceAdvantage?: boolean;
  Name: string;
  Party_ID__c?: string;
};

type Payload = { totalSize: number; records: SellerAccountRecord[] };

const EMPTY_PAYLOAD: Payload = { totalSize: 0, records: [] };
let cache: Payload | null = null;
let inFlight: Promise<Payload> | null = null;
let fetchGeneration = 0;

function incrementFetchGeneration(): void {
  fetchGeneration += 1;
}

function fetchSellerAccountList(): Promise<Payload> {
  if (cache) return Promise.resolve(cache);
  if (inFlight) return inFlight;
  fetchGeneration += 1;
  const myGen = fetchGeneration;
  inFlight = fetch(getSalesforceSellerAccountListUrl())
    .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
    .then((data: Payload) => {
      if (myGen === fetchGeneration) cache = data;
      return data;
    })
    .catch((err) => {
      if (myGen === fetchGeneration) inFlight = null;
      throw err;
    });
  return inFlight;
}

export function findSellerByName(
  records: SellerAccountRecord[],
  name: string | null | undefined
): SellerAccountRecord | undefined {
  if (!name?.trim()) return undefined;
  const nameLower = name.trim().toLowerCase();
  return records.find((record) => record.Name?.trim().toLowerCase() === nameLower);
}

export function findSellerByPartyId(
  records: SellerAccountRecord[],
  partyId: number | string | null | undefined
): SellerAccountRecord | undefined {
  if (partyId == null || partyId === '') return undefined;
  const id = String(partyId);
  return records.find((record) => record.Party_ID__c != null && String(record.Party_ID__c) === id);
}

export function findSellerById(
  records: SellerAccountRecord[],
  id: string | null | undefined
): SellerAccountRecord | undefined {
  if (!id?.trim()) return undefined;
  return records.find((record) => record.Id === id);
}

export type BoatInfoForSellerMatch = { sellerName?: string | null; partyId?: number | null };

export function matchSellerFromBoatInfo(
  records: SellerAccountRecord[],
  boatInfo: BoatInfoForSellerMatch | null | undefined
): SellerAccountRecord | undefined {
  if (!boatInfo || !records.length) return undefined;

  if (boatInfo.partyId != null) {
    const byParty = findSellerByPartyId(records, boatInfo.partyId);
    if (byParty) return byParty;
  }

  return boatInfo.sellerName ? findSellerByName(records, boatInfo.sellerName) : undefined;
}

export type UseSellerAccountListResult = {
  records: SellerAccountRecord[];
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  findSellerByName: (name: string | null | undefined) => SellerAccountRecord | undefined;
  findSellerByPartyId: (
    partyId: number | string | null | undefined
  ) => SellerAccountRecord | undefined;
  findSellerById: (id: string | null | undefined) => SellerAccountRecord | undefined;
  matchSellerFromBoatInfo: (
    boatInfo: BoatInfoForSellerMatch | null | undefined
  ) => SellerAccountRecord | undefined;
};

export function useSellerAccountList(enabled: boolean): UseSellerAccountListResult {
  const [data, setData] = useState<Payload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const loadIdRef = useRef(0);

  const load = useCallback(async () => {
    loadIdRef.current += 1;
    const myLoadId = loadIdRef.current;
    if (cache) {
      if (myLoadId === loadIdRef.current) setData(cache);
      return;
    }
    if (myLoadId === loadIdRef.current) {
      setLoading(true);
      setError(null);
    }
    try {
      const payload = await fetchSellerAccountList();
      if (myLoadId === loadIdRef.current) setData(payload);
    } catch (err) {
      if (myLoadId === loadIdRef.current) {
        setError(err instanceof Error ? err : new Error(String(err)));
        setData(EMPTY_PAYLOAD);
      }
    } finally {
      if (myLoadId === loadIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (enabled) load();
  }, [enabled, load]);

  const refetch = useCallback(() => {
    incrementFetchGeneration();
    cache = null;
    inFlight = null;
    setData(null);
    if (enabled) load();
  }, [enabled, load]);

  const records = data?.records ?? [];
  return {
    records,
    loading,
    error,
    refetch,
    findSellerByName: (name) => findSellerByName(records, name),
    findSellerByPartyId: (partyId) => findSellerByPartyId(records, partyId),
    findSellerById: (id) => findSellerById(records, id),
    matchSellerFromBoatInfo: (boatInfo) => matchSellerFromBoatInfo(records, boatInfo),
  };
}
